import { FC, useEffect, useState } from "react";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import CommonLayout from "./CommonLayout";
import FormItem from "./FormItem";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentHolidayPackage, setHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import type { HolidayPackage } from "data/types/holidayPackage";
import { useTranslation } from "react-i18next";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import AdditionalDateField from "./AdditionalDateField";


export interface AddHolidayPackage3Props { }

const PageAddHolidayPackage3: FC<AddHolidayPackage3Props> = () => {

    const { t, ready } = useTranslation("translation");
    const holidayPackage: HolidayPackage = useSelector(selectCurrentHolidayPackage);
    const dispatch = useDispatch();

    const [showFlexibleDates, setShowFlexibleDates] = useState(true);
    const [dateFields, setDateFields] = useState<any>([]);

    const handleUpdateHolidayPackage = async (field: string, value: any) => {

        if (field === "fixed_dates_ongoing") {
            value = value === "true" ? true : false;
            setShowFlexibleDates(!value);
            if (value) {
                dispatch(setHolidayPackage({ 
                    ...holidayPackage, 
                    flexible_dates_start: new Date("01/01/2023"), 
                    flexible_dates_end: new Date("01/01/2050") 
                }))
            }
        }

        if (field === "flexible_dates_start" || field === "flexible_dates_end") {
            value = new Date(value);
        }

        dispatch(setHolidayPackage({ ...holidayPackage, [field]: value }))
    };

    const _updateDefaultFields = async () => {
        dispatch(setHolidayPackage({ 
            ...holidayPackage, 
            fixed_dates_ongoing: holidayPackage?.fixed_dates_ongoing? holidayPackage.fixed_dates_ongoing : false, 
            flexible_dates: holidayPackage?.flexible_dates? holidayPackage.flexible_dates : false,
            flexible_dates_start: holidayPackage?.flexible_dates_start? holidayPackage.flexible_dates_start : undefined,
            flexible_dates_end: holidayPackage?.flexible_dates_end? holidayPackage.flexible_dates_end : undefined,
            fixed_dates: holidayPackage?.fixed_dates? holidayPackage.fixed_dates : false,
            fixed_dates_start: holidayPackage?.fixed_dates_start? holidayPackage.fixed_dates_start : [],
        }))
    }

    useEffect(() => {
        _updateDefaultFields()
        setShowFlexibleDates(!holidayPackage?.fixed_dates_ongoing);
        setDateFields(holidayPackage?.fixed_dates_start || []);
    }, []);

    function nextPageCondition() {
        // console.log("holidayPackage", holidayPackage);
        return (holidayPackage?.fixed_dates_ongoing) || ((holidayPackage?.flexible_dates_start !== undefined) && (holidayPackage?.flexible_dates_end !== undefined))
    }

    function deleteDateField(key: number) {
        let newDateFields = JSON.parse(JSON.stringify(dateFields));
        newDateFields.splice(key, 1);
        setDateFields(newDateFields);
    }

    function generateDateField() {
        let newDateFields = JSON.parse(JSON.stringify(dateFields));
        newDateFields.push("dd/mm/yyyy");
        setDateFields(newDateFields);
    }

    const handleFixedDatesUpdates = async (index: number, value: any) => {
        let newFixedDates = JSON.parse(JSON.stringify(dateFields));
        newFixedDates[index] = value;
        dispatch(setHolidayPackage({ ...holidayPackage, fixed_dates_start: newFixedDates }));
        if (newFixedDates.length > 0) {
            dispatch(setHolidayPackage({ ...holidayPackage, fixed_dates: true }));
        }
    }

    if (!ready) return null;

    return (
        <CommonLayout
            index="03"
            backtHref="/host/new-holiday-package/add-listing-2"
            nextHref={"/host/new-holiday-package/add-listing-4"}
            canGoNext={nextPageCondition()}
        >
        <>
            <h2 className="text-2xl font-semibold">{t("translation:components.holidayPackageForm.page3.title")}</h2>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
            {/* FORM */}
            <div className="space-y-8">    
                <div className="flew flex-row align-center items-center justify-between">
                    <div className="w-full">
                        <FormItem
                            label={t("translation:components.holidayPackageForm.page3.form.alwaysAvailable")}
                            desc={t("translation:components.holidayPackageForm.page3.form.alwaysAvailableDescription")}
                        >
                            <Select
                                value={holidayPackage?.fixed_dates_ongoing? "true" : "false"}
                                onChange={(e) => handleUpdateHolidayPackage("fixed_dates_ongoing", e.target.value)}
                            >
                                <option value="false">{t("translation:components.holidayPackageForm.page3.form.alwaysAvailableFalse")}</option>
                                <option value="true">{t("translation:components.holidayPackageForm.page3.form.alwaysAvailableTrue")}</option>
                            </Select>
                        </FormItem>
                    </div>
                    {
                        showFlexibleDates &&
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                            <FormItem
                                label={t("translation:components.holidayPackageForm.page3.form.startDateName")}
                                desc={t("translation:components.holidayPackageForm.page3.form.startDateDescription")}
                            >
                                <Input
                                    type="date"
                                    value={holidayPackage?.flexible_dates_start ? holidayPackage.flexible_dates_start.toISOString().split('T')[0] : ""}
                                    onChange={(e) => handleUpdateHolidayPackage("flexible_dates_start", e.target.value)}
                                />
                            </FormItem>

                            <FormItem
                                label={t("translation:components.holidayPackageForm.page3.form.endDateName")}
                                desc={t("translation:components.holidayPackageForm.page3.form.endDateDescription")}
                            >
                                <Input
                                    type="date"
                                    value={holidayPackage?.flexible_dates_end ? holidayPackage.flexible_dates_end.toISOString().split('T')[0] : ""}
                                    onChange={(e) => handleUpdateHolidayPackage("flexible_dates_end", e.target.value)}
                                />
                            </FormItem>
                        </div>
                    }
                </div>
                <FormItem
                    label={""}
                    desc={""}
                >
                    <h3 className="text-lg font-semibold mb-2">
                        {t("translation:components.holidayPackageForm.page3.form.fixedDatesSectionTitle")}
                    </h3>
                    <p className="mb-2">
                        {t("translation:components.holidayPackageForm.page3.form.fixedDatesSectionDescription")}
                    </p>
                    <div className="flex flex-col w-full align-center items-start">
                        {dateFields.map((item:string, index:number) => (
                            <div key={`date-field-${index}`} className="mb-4">
                                <AdditionalDateField
                                    key={index}
                                    onChange={handleFixedDatesUpdates}
                                    onDelete={(e) => deleteDateField(index)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-start">
                        <ButtonSecondary
                            type="button"
                            onClick={generateDateField}
                        >
                            {t("translation:components.holidayPackageForm.page3.form.addFixedDate")}
                        </ButtonSecondary>
                    </div>
                </FormItem>

            </div>
        </>
        </CommonLayout >
    );
};

export default PageAddHolidayPackage3;
